import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || '';
let logoutTimer;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('tokenExpiry');
    setUser(null);
    setIsAuthenticated(false);
    if (logoutTimer) {
      clearTimeout(logoutTimer);
    }
  }, []);

  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = localStorage.getItem('authToken');
      const expiryTime = localStorage.getItem('tokenExpiry');

      if (token && expiryTime) {
        const remainingTime = new Date(expiryTime).getTime() - new Date().getTime();
        
        if (remainingTime <= 0) {
          logout();
          setLoading(false);
          return;
        }
        
        // Set a timer for auto-logout
        logoutTimer = setTimeout(logout, remainingTime);

        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/verify`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.valid) {
              const storedUser = JSON.parse(localStorage.getItem('userData'));
              setUser(storedUser);
              setIsAuthenticated(true);
            } else {
              logout();
            }
          } else {
            logout();
          }
        } catch (error) {
          console.error('Token verification failed:', error);
          logout();
        }
      }
      setLoading(false);
    };

    checkAuthStatus();
  }, [logout]);

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        const expiryTime = new Date().getTime() + data.expires_in * 1000;
        
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('userData', JSON.stringify(data.user));
        localStorage.setItem('tokenExpiry', new Date(expiryTime).toISOString());
        
        setUser(data.user);
        setIsAuthenticated(true);
        
        // Clear any existing timer and set a new one
        if (logoutTimer) {
          clearTimeout(logoutTimer);
        }
        logoutTimer = setTimeout(logout, data.expires_in * 1000);
        
        return { success: true };
      } else {
        return { 
          success: false, 
          error: data.error || 'Login failed, please check your email and password'
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: 'Unable to connect to the authentication server, please check your network connection'
      };
    }
  };

  const value = {
    isAuthenticated,
    user,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
